import { useElements, CardNumberElement } from '@stripe/react-stripe-js';
import {
  type StripeElements,
  type PaymentMethod,
  type PaymentRequestCompleteStatus,
  type Stripe,
  type StripeCardNumberElementChangeEvent,
  type StripeCardExpiryElementChangeEvent,
  type StripeCardCvcElementChangeEvent,
} from '@stripe/stripe-js';
import { sendPaymentSetupIntentRequest, sendUpdatePaymentMethodRequest } from '../api/storeApi';

interface CardFieldValidation {
  empty: boolean;
  complete: boolean;
  error: boolean;
}

export interface CardFormValidation {
  card: CardFieldValidation;
  expiration: CardFieldValidation;
  cvc: CardFieldValidation;
}

type UpdateCardFormValidation = (validation: (validation: CardFormValidation) => CardFormValidation) => void;

export const useCardFormListeners = (updateValidation: UpdateCardFormValidation) => {
  const elements = useElements();

  return {
    handleNumberChange: ({ complete, empty, error }: StripeCardNumberElementChangeEvent) => {
      updateValidation((v) => ({ ...v, card: { complete, empty, error: Boolean(error) } }));
      if (complete) {
        elements?.getElement('cardExpiry')?.focus();
      }
    },
    handleExpirationChange: ({ complete, empty, error }: StripeCardExpiryElementChangeEvent) => {
      updateValidation((v) => ({ ...v, expiration: { complete, empty, error: Boolean(error) } }));
      if (complete) {
        elements?.getElement('cardCvc')?.focus();
      }
    },
    handleCSCChange: ({ complete, empty, error }: StripeCardCvcElementChangeEvent) => {
      updateValidation((v) => ({ ...v, cvc: { complete, empty, error: Boolean(error) } }));
    },
  };
};

const processMethodUpdate = async (stripe: Stripe, paymentMethod: PaymentMethod, paymentMethodType: string) => {
  const {
    data: {
      requires_action: requiresAction,
      stripe_payment_setup_intent_id: setupIntentId,
      stripe_payment_setup_intent_client_secret: setupIntentSecret,
    },
  } = await sendPaymentSetupIntentRequest(paymentMethod.id);

  let resolvedSetupIntentId = setupIntentId;

  if (requiresAction) {
    const { error, setupIntent } = await stripe.confirmCardSetup(setupIntentSecret, {
      payment_method: paymentMethod.id,
    });

    if (error) {
      throw new Error(error.message);
    }

    resolvedSetupIntentId = setupIntent.id;
  }

  return sendUpdatePaymentMethodRequest(resolvedSetupIntentId, paymentMethodType);
};

export const handleApplePayMethod = (
  stripe: Stripe,
  paymentMethod: PaymentMethod,
  complete: (status: PaymentRequestCompleteStatus) => void,
) => {
  void processMethodUpdate(stripe, paymentMethod, 'apple_pay').then(() => {
    complete('success');
    window.location.reload();
  });
};

export const handleGooglePayMethod = (
  stripe: Stripe,
  paymentMethod: PaymentMethod,
  complete: (status: PaymentRequestCompleteStatus) => void,
) => {
  void processMethodUpdate(stripe, paymentMethod, 'google_pay').then(() => {
    complete('success');
    window.location.reload();
  });
};

export const onChangeMethodCardSubmit = (elements: StripeElements | null, stripe: Stripe | null) => {
  if (!elements) {
    throw new Error('Elements not found');
  }

  if (!stripe) {
    throw new Error('Stripe not found');
  }

  const cardElement = elements.getElement(CardNumberElement);

  if (!cardElement) {
    throw new Error('Card element not found');
  }

  return stripe
    .createPaymentMethod({
      type: 'card',
      card: cardElement,
    })
    .then((res) => {
      if (res.error) {
        throw new Error(res.error.message);
      }

      return res.paymentMethod;
    })
    .then((paymentMethod) => processMethodUpdate(stripe, paymentMethod, 'card'));
};
